import { usVenuePlanForBoardKey } from '@/lib/polymarketUs/gameMapping';
import { usLineTargetFor } from '../ValueDashboard/us/usLineTarget';
import { baseGameKey } from '@/services/analyticsGameKey';
import type { TradeIntent } from '../ValueDashboard/GameDetailPanel';
import { gameMarketKind } from '../ValueDashboard/tradeModalBook';
import type { TradeTicketContext } from '../ValueDashboard/types';

/** Lossless map from the GameDetail page's tradeCtx (TradeTicketContext)
 *  to GameDetailPanel's TradeIntent prop shape. teamSide → side; the
 *  prefill / postOnly / orderSide carry through unchanged.
 *
 *  `polymarketUs` is the page's US link (`?venue=polymarket_us`) applied to
 *  THIS ticket: the caller passes it only for the ticket the page seeded from
 *  that URL. A Kalshi or Polymarket pill clicked afterwards keeps its own venue
 *  and price; only a US pill (an exact US contract) is a US intent by itself. */
export function tradeCtxToTradeIntent(ctx: TradeTicketContext, polymarketUs = false, usAccountId: string | null = null,
  usPill = false): TradeIntent {
  const player = ctx.game.usPlayerProp ?? ctx.game.usGameProp;
  // A US pill is an exact US contract. A `?venue=polymarket_us` link only opens
  // the US tab where G1 has a US plan for the game: on a sport US does not
  // cover (soccer, MLB) the tab would mount with nothing to show, so the ticket
  // keeps its Kalshi/Polymarket seed instead.
  const moneylineUs = gameMarketKind(ctx.game) === 'moneyline' && usVenuePlanForBoardKey(ctx.game.key) !== null;
  // #1231 — only a US PILL opens a spread or total line on the US tab, and only
  // where the host can name that exact line; a URL seed on a line stays K/P.
  const lineKind = usPill && usVenuePlanForBoardKey(baseGameKey(ctx.game.key)) !== null
    ? usLineTargetFor(ctx.game).kind : null;
  const pillUs = usPill && (moneylineUs || lineKind === 'spread' || lineKind === 'total');
  const us = !!player || polymarketUs && moneylineUs || pillUs;
  // A pill opens the US tab for the page's own accounts, never a link's.
  if (usPill) usAccountId = null;
  return {
    side: ctx.teamSide,
    liquiditySeed: ctx.liquiditySeed ?? ctx.meta?.liquiditySeed,
    venue: ctx.venue,
    prefillPrice: ctx.prefillPrice,
    initialOrderSide: ctx.initialOrderSide,
    postOnly: ctx.postOnly,
    // #987 — `?venue=polymarket_us` opens the panel's US tab. US joins by exact
    // line, so the flag only applies where the host can resolve one.
    ...(us ? { polymarketUs: true } : {}),
    ...(player ? { polymarketUsMarketSlug: player.marketSlug } : {}),
    // #1006 — `&usAccount=` (a Tracker row's link): the US tab acts for that
    // account only. Never applied without the US tab it belongs to.
    ...(us && usAccountId ? { polymarketUsAccountId: usAccountId } : {}),
  };
}
